import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium tracking-[-0.01em] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary: 'bg-accent text-accent-foreground border border-accent hover:opacity-90',
        secondary: 'bg-paper text-ink border border-line-2 hover:bg-paper-2',
        ghost: 'bg-transparent text-ink border border-transparent hover:bg-paper-2',
        danger: 'bg-paper text-destructive border border-line-2 hover:bg-destructive-bg',
        success: 'bg-success text-white border border-success hover:opacity-90',
        link: 'bg-transparent text-ink underline underline-offset-[3px] border-none',
      },
      size: {
        sm: 'h-8 px-3 text-[13px] gap-1.5',
        md: 'h-10 px-3.5 text-sm',
        lg: 'h-12 px-[18px] text-[15px] gap-2.5',
        xl: 'h-14 px-[22px] text-base gap-3',
        icon: 'h-9 w-9 p-0',
      },
      full: { true: 'w-full', false: '' },
    },
    compoundVariants: [
      // Link variant ignores size padding/height — it behaves like inline text.
      { variant: 'link', class: 'h-auto p-0' },
    ],
    defaultVariants: { variant: 'primary', size: 'md', full: false },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, full, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size, full }), className)}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { buttonVariants };
